import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState } from '../components/common/EmptyState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  FileText,
  Eye,
  MessageSquare,
  Clock,
  Target,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Filter,
  Download,
  Loader2
} from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

// =========================================================
// SUB-COMPONENTS (unchanged from original)
// =========================================================

// Stat Card Component
function StatCard({ title, value, icon: Icon, format = 'number' }) {
  const { t } = useTranslation();
  const formattedValue = format === 'currency'
    ? `$${Number(value || 0).toLocaleString()}`
    : format === 'percent'
      ? `${value || 0}%`
      : Number(value || 0).toLocaleString();

  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{formattedValue}</p>
          </div>
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-6 w-6 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Simple Bar Chart Component
function SimpleBarChart({ data, valueKey, labelKey, maxValue }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">{t("analytics.noDataAvailableFor")}</p>;
  }
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0), 1);

  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={index} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground truncate max-w-[60%]">{item[labelKey]}</span>
            <span className="font-medium">${(item[valueKey] || 0).toLocaleString()}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${Math.max((item[valueKey] / max) * 100, 2)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// Deal Pipeline Funnel
function DealFunnel({ data }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">{t("analytics.noDealsFound")}</p>;
  }
  const total = data.reduce((sum, d) => sum + (d.count || 0), 0) || 1;
  const colors = {
    'Draft': 'bg-slate-400',
    'Active': 'bg-blue-500',
    'Pending Docs': 'bg-amber-400',
    'Pending Sign': 'bg-purple-500',
    'Pending Pay': 'bg-emerald-500',
    'On Hold': 'bg-orange-400',
    'Closed Won': 'bg-green-500',
    'Completed': 'bg-green-600',
    'Closed Lost': 'bg-red-400',
    'Cancelled': 'bg-red-500',
  };

  return (
    <div className="space-y-2 h-[250px] overflow-y-auto pr-2 custom-scrollbar">
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="w-24 text-sm text-muted-foreground truncate">{item.status}</div>
          <div className="flex-1 h-8 bg-muted rounded-md overflow-hidden">
            <div
              className={`h-full ${colors[item.status] || 'bg-primary'} flex items-center px-2 transition-all duration-500`}
              style={{ width: `${Math.max((item.count / total) * 100, 10)}%` }}
            >
              <span className="text-xs text-white font-medium">{item.count}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Top Clients Table
function TopClientsTable({ data }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">{t("analytics.noClientDataAvailable")}</p>;
  }
  return (
    <div className="space-y-1">
      {/* Column Headers */}
      <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-2 pb-1 border-b border-muted">
        <span className="text-xs text-muted-foreground font-medium">{t("analytics.client")}</span>
        <span className="text-xs text-muted-foreground font-medium w-16 text-center">{t("analytics.invoices")}</span>
        <span className="text-xs text-muted-foreground font-medium w-24 text-right">{t("analytics.revenue")}</span>
      </div>
      {data.map((client, index) => (
        <div key={index} className="grid grid-cols-[1fr_auto_auto] gap-4 items-center p-2 rounded-lg hover:bg-muted/50">
          {/* Rank + Name */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
              {index + 1}
            </div>
            <span className="font-medium truncate text-sm">{client.name}</span>
          </div>
          {/* Invoice count — centred */}
          <span className="w-16 text-center text-sm text-muted-foreground">{client.invoice_count}</span>
          {/* Revenue — right-aligned */}
          <span className="w-24 text-right font-semibold text-green-600 text-sm">
            ${Number(client.revenue).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

// Invoice Health Card Component
function InvoiceHealthCard({ data }) {
  const { t } = useTranslation();
  if (!data) return <Skeleton className="h-[250px]" />;

  const totalAmount = data.paid.amount + data.pending.amount + data.overdue.amount;

  const StatRow = ({ label, colorClass, dataObj }) => {
    if (!dataObj) return null;
    return (
      <div className="flex justify-between items-center py-3 border-b last:border-0 border-muted">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${colorClass}`} />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold">${Number(dataObj.amount).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">{dataObj.count} invoice{dataObj.count !== 1 ? 's' : ''}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 h-full flex flex-col justify-between">
      <div className="flex items-end gap-2 mb-2">
        <h2 className="text-3xl font-bold">${Number(totalAmount).toLocaleString()}</h2>
        <span className="text-sm text-muted-foreground pb-1">{t("analytics.totalPipeline")}</span>
      </div>

      <div className="space-y-2 flex-1 flex flex-col justify-center">
        <StatRow label={t("analytics.paid") || "Paid"} colorClass="bg-green-500" dataObj={data.paid} />
        <StatRow label={t("analytics.pendingPayment") || "Pending Payment"} colorClass="bg-amber-400" dataObj={data.pending} />
        <StatRow label="Overdue" colorClass="bg-red-500" dataObj={data.overdue} />
      </div>
    </div>
  );
}

// Activity Feed
function ActivityFeed({ activities }) {
  const { t } = useTranslation();
  if (!activities || activities.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">{t("analytics.noRecentActivityFor")}</p>;
  }
  const icons = {
    deal_won: <DollarSign className="h-4 w-4 text-green-500" />,
    new_lead: <Users className="h-4 w-4 text-blue-500" />,
    invoice_paid: <FileText className="h-4 w-4 text-amber-500" />,
    profile_view: <Eye className="h-4 w-4 text-purple-500" />
  };

  return (
    <div className="space-y-3">
      {activities.map((activity, index) => (
        <div key={index} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            {icons[activity.type] || <Clock className="h-4 w-4 text-muted-foreground" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{activity.description}</p>
            <p className="text-xs text-muted-foreground">{activity.time}</p>
          </div>
          {activity.value != null && (
            <span className="text-sm font-semibold text-green-600">
              +${Number(activity.value).toLocaleString()}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// Role-based dashboard widget (reuses existing /api/insights/dashboard/{role})
function RolePerformanceTab({ user }) {
  const { t } = useTranslation();
  const role = user?.demo_current_role || user?.role || 'business';

  const { data: roleData, isLoading } = useQuery({
    queryKey: ['insights-dashboard', role],
    queryFn: () => api.get(`/insights/dashboard/${role}`),
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${role === 'business' || role === 'seller' ? '3' : '4'} gap-4`}>
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }

  if (!roleData) return null;

  const heroStats = roleData.hero_stats || [];

  // Dynamically size grid columns based on number of stats returned
  const gridColsClass = heroStats.length === 3
    ? "grid-cols-1 sm:grid-cols-3 lg:grid-cols-3"
    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";

  return (
    <div className="space-y-6">
      <div className={`grid ${gridColsClass} gap-4`}>
        {heroStats.map((stat, i) => (
          <Card key={i} data-testid={`role-stat-${i}`} className="border-l-4 border-l-primary/30">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center min-h-[88px]">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{stat.label}</p>
              <p className="text-3xl font-bold mt-1 leading-none">{String(stat.value)}</p>
              {stat.trend && (
                <p className={`text-xs mt-2 ${stat.trend_up ? 'text-green-600' : stat.trend_up === false ? 'text-red-600' : 'text-muted-foreground'}`}>
                  {stat.trend}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lead pipeline or sourcing pipeline */}
      {(roleData.lead_pipeline || roleData.sourcing_pipeline) && (
        <Card>
          <CardHeader>
            <CardTitle>{t("analytics.pipelineOverview")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(roleData.lead_pipeline || roleData.sourcing_pipeline || {}).map(([key, val]) => (
                <div key={key} className="text-center">
                  <p className="text-2xl font-bold">{val}</p>
                  <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =========================================================
// LOADING SKELETON
// =========================================================
function AnalyticsSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in p-6" data-testid="analytics-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Skeleton className="h-80" />
        <Skeleton className="h-80" />
      </div>
    </div>
  );
}

// =========================================================
// MAIN PAGE
// =========================================================
export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [timePeriod, setTimePeriod] = useState('30d');
  const [isExporting, setIsExporting] = useState(false);

  // Show congratulations toast when Stripe redirects back after successful plan upgrade
  useEffect(() => {
    if (searchParams.get('subscribed') === 'true') {
      toast.success('Welcome to Pro! All Pro features are now unlocked.', { duration: 6000 });
      queryClient.invalidateQueries(['org-plan']);
      queryClient.invalidateQueries(['subscription-status']);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Real API queries ----
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics-overview', timePeriod],
    queryFn: () => api.get(`/analytics/overview?period=${timePeriod}`),
    staleTime: 1000 * 60 * 2,
  });

  const { data: dealsData, isLoading: dealsLoading } = useQuery({
    queryKey: ['analytics-deals', timePeriod],
    queryFn: () => api.get(`/analytics/deals?period=${timePeriod}`),
    staleTime: 1000 * 60 * 2,
  });

  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['analytics-revenue', timePeriod],
    queryFn: () => api.get(`/analytics/revenue?period=${timePeriod}`),
    staleTime: 1000 * 60 * 2,
  });

  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['analytics-clients', timePeriod],
    queryFn: () => api.get(`/analytics/clients?period=${timePeriod}`),
    staleTime: 1000 * 60 * 2,
  });

  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['analytics-invoice-health', timePeriod],
    queryFn: () => api.get(`/analytics/invoice-health?period=${timePeriod}`),
    staleTime: 1000 * 60 * 2,
  });

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['analytics-activity', timePeriod],
    queryFn: () => api.get(`/analytics/activity?period=${timePeriod}`),
    staleTime: 1000 * 60 * 2,
  });

  const isLoading = overviewLoading || dealsLoading || revenueLoading || clientsLoading || healthLoading || activityLoading;

  // ---- CSV Export ----
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const url = `${api.constructor ? '' : ''}${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/export?period=${timePeriod}&format=csv`;
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `analytics_${timePeriod}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(objectUrl);
      toast.success('Analytics exported successfully');
    } catch (err) {
      toast.error('Failed to export analytics');
    } finally {
      setIsExporting(false);
    }
  }, [timePeriod]);

  // ---- Safe data accessors ----
  const overview = overviewData?.overview || {};
  const deals_by_status = dealsData?.deals_by_status || [];
  const monthly_revenue = revenueData?.monthly_revenue || [];
  const top_products = revenueData?.top_products || [];
  const top_clients = clientsData?.top_clients || [];
  const invoice_health = healthData?.invoice_health || null;
  const recent_activity = activityData?.recent_activity || [];

  if (isLoading) return <AnalyticsSkeleton />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Empty analytics state */}
      {!overviewLoading && overviewData && (overviewData.total_deals === 0 && overviewData.total_clients === 0) && (
        <EmptyState
          icon="analysis.svg"
          title="No analytics data yet"
          description="Analytics will populate as you create deals, close contracts, and generate invoices. Start by creating your first deal."
          action={
            <Link to="/deals">
              <Button>{t("analytics.goToDeals")}</Button>
            </Link>
          }
        />
      )}


      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("analytics.analyticsDashboard")}</h1>
          <p className="text-muted-foreground">{t("analytics.trackYourBusinessPerformance")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[160px]" data-testid="time-period-select">
              <Calendar className="h-4 w-4 mr-2 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">{t("analytics.last7Days")}</SelectItem>
              <SelectItem value="30d">{t("analytics.last30Days")}</SelectItem>
              <SelectItem value="90d">{t("analytics.last90Days")}</SelectItem>
              <SelectItem value="1y">{t("analytics.lastYear")}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            data-testid="export-button"
          >
            {isExporting
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />
            }
            Export
          </Button>
        </div>
      </div>

      {/* Tabs: Overview / My Performance */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t("analytics.overview")}</TabsTrigger>
          <TabsTrigger value="performance" data-testid="performance-tab">{t("analytics.myPerformance")}</TabsTrigger>
        </TabsList>

        {/* ---- OVERVIEW TAB ---- */}
        <TabsContent value="overview" className="space-y-6 mt-4">

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              title={t("analytics.totalRevenue") || "Total Revenue"}
              value={overview.total_revenue}
              change={overview.revenue_change}
              icon={DollarSign}
              format="currency"
            />
            <StatCard
              title={t("analytics.totalDeals") || "Total Deals"}
              value={overview.total_deals}
              change={overview.deals_change}
              icon={FileText}
            />
            <StatCard
              title={t("analytics.conversionRate") || "Conversion Rate"}
              value={overview.conversion_rate}
              change={overview.conversion_change}
              icon={Target}
              format="percent"
            />
            <StatCard
              title={t("analytics.avgDealValue") || "Avg Deal Value"}
              value={overview.avg_deal_value}
              change={overview.avg_deal_change}
              icon={TrendingUp}
              format="currency"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Deal Pipeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  {t("analytics.dealPipeline") || "Deal Pipeline"}
                </CardTitle>
                <CardDescription>{t("analytics.currentDealsByStatus")}</CardDescription>
              </CardHeader>
              <CardContent>
                <DealFunnel data={deals_by_status} />
              </CardContent>
            </Card>

            {/* Monthly Revenue */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  {t("analytics.monthlyRevenue") || "Monthly Revenue"}
                </CardTitle>
                <CardDescription>{t("analytics.revenueTrendsOverLast")}</CardDescription>
              </CardHeader>
              <CardContent>
                <SimpleBarChart
                  data={monthly_revenue}
                  valueKey="revenue"
                  labelKey="month"
                />
              </CardContent>
            </Card>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

            {/* Top Clients */}
            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  {t("analytics.topClientsByRevenue") || "Top Clients by Revenue"}
                </CardTitle>
                <CardDescription>{t("analytics.accountsGeneratingTheMost")}</CardDescription>
              </CardHeader>
              <CardContent>
                <TopClientsTable data={top_clients} />
              </CardContent>
            </Card>

            {/* Invoice Health */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  {t("analytics.invoiceHealth") || "Invoice Health"}
                </CardTitle>
                <CardDescription>{t("analytics.currentCashFlowOverview")}</CardDescription>
              </CardHeader>
              <CardContent>
                <InvoiceHealthCard data={invoice_health} />
              </CardContent>
            </Card>
          </div>

          {/* Activity Feed */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Recent Activity
              </CardTitle>
              <CardDescription>{t("analytics.latestBusinessActivitiesAnd")}</CardDescription>
            </CardHeader>
            <CardContent>
              <ActivityFeed activities={recent_activity} />
            </CardContent>
          </Card>

        </TabsContent>

        {/* ---- MY PERFORMANCE TAB (role-based) ---- */}
        <TabsContent value="performance" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("analytics.myPerformance")}</CardTitle>
              <CardDescription>
                Role-based metrics for your account —{' '}
                <span className="capitalize font-medium">
                  {(user?.demo_current_role || user?.role || 'business').replace(/_/g, ' ')}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RolePerformanceTab user={user} />
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
